/**
 * The deal table (ADR-0060): what you give, what you ask, and what they make of it.
 *
 * The rival's verdict is worked out as you choose, by the same `appraise` the AI uses
 * to answer — so "they want about 40 gold more" is exactly what it is, and proposing
 * the deal can never surprise you.
 *
 * At war the same table is the peace table (ADR-0071): gold, technologies and cities
 * as terms, judged by `appraisePeace`.
 */

import type { Engine } from '../core/engine.js';
import { ERROR_TEXT } from '../core/engine.js';
import { appraise, dealBlocker } from '../core/diplomacy/deals.js';
import { appraisePeace, peaceBlocker } from '../core/diplomacy/peace.js';
import { citiesOf } from '../core/cities/index.js';
import { isCapital } from '../core/cities/economy.js';
import { contactsOf } from '../core/diplomacy/borders.js';
import { atWar, truceLeft } from '../core/diplomacy/index.js';
import { relationOf } from '../core/diplomacy/relation.js';
import { knows } from '../core/research/knowledge.js';
import { resourceAccess } from '../core/resources/access.js';
import { resourceKnown, resourceUsed } from '../core/resources/known.js';
import type { Civ, DealItem } from '../core/state/types.js';

export interface DealScreen {
  open(civId: number): void;
  /** The peace table, with a civilisation you are at war with. */
  openPeace(civId: number): void;
  close(): void;
  readonly isOpen: boolean;
}

export interface DealScreenOptions {
  getEngine: () => Engine;
  onChanged: () => void;
  log: (message: string, kind?: 'ok' | 'err' | 'note') => void;
}

export function createDealScreen(options: DealScreenOptions): DealScreen {
  const root = document.createElement('div');
  root.className = 'cities-screen';
  root.hidden = true;
  root.innerHTML = `
    <div class="cities-panel deal-panel">
      <header>
        <h2 id="dl-title">Deal</h2>
        <div class="cs-sub" id="dl-sub"></div>
        <button id="dl-close" title="Close (Esc)">Close</button>
      </header>
      <div class="cts-body">
        <div class="deal-columns">
          <section><h3>You give</h3><div id="dl-give"></div></section>
          <section><h3>Both agree</h3><div id="dl-both"></div></section>
          <section><h3>They give</h3><div id="dl-get"></div></section>
        </div>
        <div class="deal-verdict" id="dl-verdict"></div>
        <footer class="dialog-actions">
          <button id="dl-demand" class="danger" title="Ask for what is in 'They give', offering nothing — they will remember it">Demand</button>
          <button id="dl-propose" class="primary">Propose</button>
        </footer>
      </div>
    </div>`;
  document.body.append(root);
  const $ = <T extends HTMLElement>(id: string): T => root.querySelector(`#${id}`) as T;
  let partner = -1;
  let peace = false;

  const techOptions = (from: Civ, to: Civ): number[] =>
    from.techs.filter((t) => !knows(to, t)).filter((t) => options.getEngine().content.technologies.get(t).prerequisites.every((p) => knows(to, p)));

  /** Strategic resources `from` draws from its own land that `to` can see and lacks (ADR-0063). */
  function resourceOptions(from: Civ, to: Civ): number[] {
    const { state, content } = options.getEngine();
    const giver = resourceAccess(state, content, from.id);
    const taker = resourceAccess(state, content, to.id);
    return content.resources.all
      .filter((r) => r.class === 'strategic' && resourceUsed(content, r.id))
      .map((r) => r.id)
      .filter((r) => (giver.own & (1 << r)) !== 0 && ((taker.own | taker.traded) & (1 << r)) === 0 && resourceKnown(content, to, r));
  }

  /** Cities `from` could hand over in peace: never its capital, never its last (ADR-0071). */
  function cityOptions(from: Civ): string {
    const { state, content } = options.getEngine();
    const cities = citiesOf(state, from.id);
    if (cities.length < 2) return '';
    return cities
      .filter((c) => !isCapital(content, c))
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((c) => `<label class="deal-item"><input type="checkbox" data-city="${c.id}"> ${escapeHtml(c.name)} <span class="dim">size ${c.population}</span></label>`)
      .join('');
  }

  function side(id: string, from: Civ, to: Civ): string {
    const { content } = options.getEngine();
    const cities = peace ? cityOptions(from) : '';
    const resources = peace ? '' : resourceOptions(from, to)
      .map((r) => `<label class="deal-item"><input type="checkbox" data-resource="${r}"> ${escapeHtml(content.resources.get(r).name)} <span class="dim">for ${content.rules.diplomacy.paymentTurns} turns</span></label>`)
      .join('');
    const techs = techOptions(from, to)
      .map((t) => `<label class="deal-item"><input type="checkbox" data-tech="${t}"> ${escapeHtml(content.technologies.get(t).name)}</label>`)
      .join('');
    return (
      `<label class="deal-item">Gold <input type="number" min="0" max="${from.treasury}" step="5" value="0" data-gold> <span class="dim">of ${from.treasury}</span></label>` +
      `<label class="deal-item">Gold a turn <input type="number" min="0" max="99" step="1" value="0" data-gpt> <span class="dim">for ${content.rules.diplomacy.paymentTurns} turns</span></label>` +
      (techs ? `<div class="deal-sub">Technologies</div>${techs}` : `<div class="dim deal-sub">No technologies ${id === 'give' ? 'they lack' : 'you lack'} that could be traded.</div>`) +
      (peace
        ? cities
          ? `<div class="deal-sub">Cities <span class="dim">— handed over with the peace</span></div>${cities}`
          : '<div class="dim deal-sub">No city that could be handed over (never the capital or a last city).</div>'
        : resources
          ? `<div class="deal-sub">Strategic resources <span class="dim">— shared, not lost</span></div>${resources}`
          : `<div class="dim deal-sub">No strategic resources ${id === 'give' ? 'they lack' : 'you lack'} that could be shared.</div>`)
    );
  }

  function read(container: HTMLElement): DealItem[] {
    const items: DealItem[] = [];
    for (const box of container.querySelectorAll<HTMLInputElement>('input[data-tech]')) {
      if (box.checked) items.push({ kind: 'tech', tech: Number(box.dataset['tech']) });
    }
    for (const box of container.querySelectorAll<HTMLInputElement>('input[data-resource]')) {
      if (box.checked) items.push({ kind: 'resource', resource: Number(box.dataset['resource']) });
    }
    for (const box of container.querySelectorAll<HTMLInputElement>('input[data-city]')) {
      if (box.checked) items.push({ kind: 'city', city: Number(box.dataset['city']) });
    }
    const gold = Math.floor(Number(container.querySelector<HTMLInputElement>('input[data-gold]')?.value ?? 0));
    if (gold > 0) items.push({ kind: 'gold', amount: gold });
    const gpt = Math.floor(Number(container.querySelector<HTMLInputElement>('input[data-gpt]')?.value ?? 0));
    if (gpt > 0) items.push({ kind: 'goldPerTurn', amount: gpt });
    return items;
  }

  function current(): { give: DealItem[]; get: DealItem[] } {
    const give = read($('dl-give'));
    const get = read($('dl-get'));
    if (peace) return { give, get };
    if ($<HTMLInputElement>('dl-borders')?.checked) give.push({ kind: 'openBorders' });
    const against = Number($<HTMLSelectElement>('dl-ally')?.value ?? -1);
    if (against >= 0) give.push({ kind: 'alliance', against });
    return { give, get };
  }

  function verdict(): void {
    const engine = options.getEngine();
    const { state, content } = engine;
    const me = engine.activeCiv;
    const { give, get } = current();
    const out = $('dl-verdict');
    if (peace) {
      // Plain peace is a fair offer too: no terms at all.
      const refused = peaceBlocker(state, content, me, partner, give, get);
      ($('dl-propose') as HTMLButtonElement).disabled = refused !== null;
      if (refused) {
        out.className = 'deal-verdict dim';
        out.textContent = ERROR_TEXT[refused];
        return;
      }
      const view = appraisePeace(state, content, partner, me, give, get);
      out.className = `deal-verdict ${view.accept ? 'ok' : 'bad'}`;
      out.textContent = view.accept ? 'They would make peace on these terms.' : `They would refuse: ${view.reason}.`;
      return;
    }
    const blocked = give.length + get.length > 0 ? dealBlocker(state, content, me, partner, give, get) : 'EmptyDeal';
    ($('dl-propose') as HTMLButtonElement).disabled = blocked !== null;
    ($('dl-demand') as HTMLButtonElement).disabled = blocked !== null || give.length > 0 || get.length === 0;
    if (blocked) {
      out.className = 'deal-verdict dim';
      out.textContent = blocked === 'EmptyDeal' ? 'Choose what to give and what to ask for.' : ERROR_TEXT[blocked];
      return;
    }
    const view = appraise(state, content, partner, me, give, get);
    out.className = `deal-verdict ${view.accept ? 'ok' : 'bad'}`;
    out.textContent = view.accept ? 'They would accept this.' : `They would refuse: ${view.reason}.`;
  }

  function render(): void {
    const engine = options.getEngine();
    const { state } = engine;
    const me = state.civs[engine.activeCiv];
    const them = state.civs[partner];
    if (!me || !them) return;
    $('dl-title').textContent = peace ? `Peace with ${them.name}` : `Deal with ${them.name}`;
    $('dl-sub').textContent = `Your treasury ${me.treasury}g · theirs ${them.treasury}g`;
    $('dl-give').innerHTML = side('give', me, them);
    $('dl-get').innerHTML = side('get', them, me);
    $('dl-both').parentElement!.hidden = peace;
    $('dl-demand').hidden = peace;
    ($('dl-propose') as HTMLButtonElement).textContent = peace ? 'Offer peace' : 'Propose';
    if (peace) {
      for (const input of root.querySelectorAll<HTMLInputElement>('#dl-give input, #dl-get input')) {
        input.addEventListener('input', verdict);
        input.addEventListener('change', verdict);
      }
      verdict();
      return;
    }

    const relation = relationOf(state, me.id, partner);
    const enemies = contactsOf(state, me.id)
      .filter((id) => id !== partner && contactsOf(state, partner).includes(id))
      .filter((id) => !relation?.alliances.includes(id) && truceLeft(state, me.id, id) === 0 && truceLeft(state, partner, id) === 0);
    $('dl-both').innerHTML =
      (relation?.openBorders
        ? '<div class="dim deal-item">Your borders are already open to each other.</div>'
        : '<label class="deal-item"><input type="checkbox" id="dl-borders"> Open borders <span class="dim">— each side\'s units may cross the other\'s land</span></label>') +
      (enemies.length
        ? `<label class="deal-item">Alliance against <select id="dl-ally"><option value="-1">nobody</option>${enemies
            .map((id) => `<option value="${id}">${escapeHtml(state.civs[id]!.name)}${atWar(state, me.id, id) ? ' (your enemy)' : ''}</option>`)
            .join('')}</select></label><div class="dim deal-sub">An alliance means you both go to war with them.</div>`
        : '<div class="dim deal-item">No one you could ally against.</div>');
    for (const input of root.querySelectorAll<HTMLInputElement | HTMLSelectElement>('input, select')) {
      input.addEventListener('input', verdict);
      input.addEventListener('change', verdict);
    }
    verdict();
  }

  function sendPeace(): void {
    const engine = options.getEngine();
    const { give, get } = current();
    const result = engine.apply({ t: 'ProposePeace', to: partner, give, get });
    if (!result.ok) {
      options.log(ERROR_TEXT[result.error], 'err');
      return;
    }
    const name = engine.state.civs[partner]?.name ?? 'They';
    const made = result.value.some((e) => e.t === 'PeaceMade');
    options.log(made ? `${name} accepts. You are at peace.` : `${name} refuses your offer of peace.`, made ? 'ok' : 'err');
    options.onChanged();
    close();
  }

  function send(demand: boolean): void {
    if (peace) return sendPeace();
    const engine = options.getEngine();
    const { give, get } = current();
    const result = engine.apply({ t: 'ProposeDeal', to: partner, give: demand ? [] : give, get, demand });
    if (!result.ok) {
      options.log(ERROR_TEXT[result.error], 'err');
      return;
    }
    const name = engine.state.civs[partner]?.name ?? 'They';
    const made = result.value.some((e) => e.t === 'DealMade');
    const refusal = result.value.find((e) => e.t === 'DealRefused');
    if (made) options.log(demand ? `${name} gives in to your demand.` : `${name} accepts the deal.`, 'ok');
    else options.log(`${name} refuses${refusal && refusal.t === 'DealRefused' && refusal.reason !== 'Refused' ? `: ${refusal.reason}` : ''}.`, 'err');
    options.onChanged();
    if (made) close();
    else render();
  }

  function open(civId: number): void {
    peace = false;
    show(civId);
  }
  function openPeace(civId: number): void {
    peace = true;
    show(civId);
  }
  function show(civId: number): void {
    partner = civId;
    // Opened over the Rivals screen: come last in the page, so it sits on top.
    document.body.append(root);
    root.hidden = false;
    render();
  }
  function close(): void {
    root.hidden = true;
  }
  $('dl-close').addEventListener('click', close);
  $('dl-propose').addEventListener('click', () => send(false));
  $('dl-demand').addEventListener('click', () => send(true));
  root.addEventListener('click', (event) => {
    if (event.target === root) close();
  });
  return {
    open,
    openPeace,
    close,
    get isOpen(): boolean {
      return !root.hidden;
    },
  };
}

const escapeHtml = (text: string): string => text.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
